/**
 * UnifiedPostCell — single row used by every post-displaying surface:
 *  - Post examples tab (projects/{pid}/post_examples/*)
 *  - Posts tab inside TopicDetailPanel (draft review + applied-to-topic)
 *  - Sprint posts on the strategy canvas (sprints/{sid}/posts/*)
 *  - Any future applied-post list (e.g. canonical content_posts/*)
 *
 * The cell is a purely presentational component: it takes a `PostLike` and
 * optional metadata chips / right-side action slot and renders the same
 * bordered Box layout everywhere.
 *
 * Design note: the visual treatment intentionally matches the existing
 * `PostExamplesTab` row so the three surfaces converge on a single shape.
 */

import React from 'react';
import { Box, Stack, Typography, Chip } from '@mui/material';
import type { PostLike } from '../../utils/postLike';

export interface UnifiedPostCellProps {
  post: PostLike;
  /**
   * Extra chips rendered to the right of platform chips (e.g. "N linked topics"
   * for post examples, format/status chip for content posts).
   */
  metaChips?: React.ReactNode;
  /** Right-side action slot (edit, delete, drag handle, etc.). */
  rightActions?: React.ReactNode;
  /** Click handler on the cell body. Leave undefined for non-interactive display. */
  onClick?: () => void;
  /**
   * Optional testid override. Default `post-like-row-{ownerKind}-{id}` keeps
   * the selector registry consistent; callers can pass a more specific one
   * (e.g. `topic-example-row-{id}`) for back-compat with existing E2E.
   */
  testId?: string;
  /** Compact layout for dense lists (e.g. inside SprintNode). */
  dense?: boolean;
}

export const UnifiedPostCell: React.FC<UnifiedPostCellProps> = ({
  post,
  metaChips,
  rightActions,
  onClick,
  testId,
  dense,
}) => {
  const resolvedTestId = testId ?? `post-like-row-${post.ownerKind}-${post.id}`;
  const title = post.title ?? post.hook ?? '(untitled)';
  const pad = dense ? 1 : 1.5;
  const interactive = !!onClick;

  return (
    <Box
      sx={{
        p: pad,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        ...(interactive && {
          '&:hover': { borderColor: 'primary.main' },
          cursor: 'pointer',
        }),
      }}
      onClick={onClick}
      data-testid={resolvedTestId}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 0.5 }}
            flexWrap="wrap"
            useFlexGap
          >
            <Typography variant="subtitle2" noWrap sx={{ flex: '0 1 auto', minWidth: 0 }}>
              {title}
            </Typography>
            {(post.platforms ?? []).map((p) => (
              <Chip key={p} size="small" label={p} variant="outlined" />
            ))}
            {/* Wave 2 — goal-driven generation chips. Both render only when the
                post carries the field; author_posts (scraped corpus) leave
                them blank, content_posts (AI output) populate them. */}
            {post.goal && (
              <Chip
                key="goal"
                size="small"
                label={`🎯 ${post.goal}`}
                variant="outlined"
                color="primary"
                sx={{ '& .MuiChip-label': { fontSize: '0.7rem' } }}
              />
            )}
            {post.templateId && (
              <Chip
                key="template"
                size="small"
                label={`📋 ${post.templateId}`}
                variant="outlined"
                sx={{ '& .MuiChip-label': { fontSize: '0.7rem' } }}
              />
            )}
            {metaChips}
          </Stack>
          {post.hook && (
            <Typography variant="body2" sx={{ mb: 0.25 }} noWrap>
              {post.hook}
            </Typography>
          )}
          {post.body && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: dense ? 1 : 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {post.body}
            </Typography>
          )}
        </Box>
        {rightActions && (
          <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
            {rightActions}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default UnifiedPostCell;
